import axios from 'axios';
import Cookies from 'universal-cookie';
import jwt_decode from 'jwt-decode';
import { store } from 'redux/store';
import { useAppSelector } from 'redux/hooks';
import { setAuth, type AuthUser, type NusSession } from 'redux/slice/authSlice';

const cookies = new Cookies();
let loading: Promise<NusSession | null> | undefined;

// Decode existing password tokens for UI state; the backend validates API access.
function passwordUser(): AuthUser | null {
  const tokens = cookies.get('tokens');
  try {
    return tokens ? jwt_decode<AuthUser>(tokens.accessToken) : null;
  } catch {
    cookies.remove('tokens', { path: '/' });
    return null;
  }
}

function publish(session: NusSession | null) {
  store.dispatch(
    setAuth({
      ready: true,
      session,
      user: session
        ? session.status === 'authenticated'
          ? session.user
          : null
        : passwordUser()
    })
  );
}

export const getAuth = () => store.getState().auth;
export const useAuth = () => useAppSelector((state) => state.auth);

// Read the HttpOnly session through the backend. Keep claims and CSRF state in memory only.
// Cookie request configuration: https://axios-http.com/docs/req_config
export function loadSession(force = false): Promise<NusSession | null> {
  // Share an in-flight lookup; force reloads the rotated session after registration.
  if (loading) return loading;
  if (getAuth().ready && !force) return Promise.resolve(getAuth().session);
  // Use Axios directly because the shared API interceptor waits for this lookup.
  loading = axios
    .get<NusSession>(`${import.meta.env.VITE_API_URL}/public/oidc/session`, {
      withCredentials: true,
      timeout: 10000
    })
    .then(({ data }) => {
      if (
        !['authenticated', 'profile_required'].includes(data.status) ||
        !data.csrfToken ||
        (data.status === 'authenticated' ? !data.user?.userId : !data.email)
      ) {
        throw new Error('Invalid sign-in response');
      }
      cookies.remove('tokens', { path: '/' });
      publish(data);
      return data;
    })
    .catch((error) => {
      if (
        force &&
        (!axios.isAxiosError(error) || error.response?.status !== 401)
      ) {
        throw error;
      }
      publish(null);
      return null;
    })
    .finally(() => {
      loading = undefined;
    });
  return loading;
}

export function passwordSignedIn(tokens: {
  accessToken: string;
  refreshToken: string;
}) {
  cookies.set('tokens', tokens, { path: '/', secure: true, sameSite: true });
  publish(null);
}

export function clearAuth() {
  cookies.remove('tokens', { path: '/' });
  publish(null);
}
